import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  FormControl,
  FormGroup,
  InputAdornment,
  InputLabel,
  TextField,
} from "@mui/material";
import { countries, Country } from "country-data";

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium" | "large";
  placeholder?: string;
}

export default function PhoneNumberInput({
  value,
  onChange,
  label,
  className,
  disabled,
  required,
  size = "medium",
  placeholder,
}: PhoneNumberInputProps) {
  const option = countries.all.filter(
    (country: Country) => country.status === "assigned"
  );
  let code = "234";
  let phone = "";
  const [open, setOpen] = useState(false);
  const sqRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  try {
    const valueParts = value.split("-");
    code = valueParts[0] || code;
    phone = valueParts[1] || "";
    phone = phone.replace(/\D/g, "");
  } catch (ex) {}

  const returnDef = useCallback(
    (val: string) => onChange && onChange(val),
    [onChange]
  );

  useEffect(() => {
    returnDef(code + "-" + phone);
  }, [returnDef, code, phone]);

  function handleChange(newVal: string | null) {
    setOpen(false);
    try {
      const cod = newVal || code;
      onChange && onChange(cod.replace("+", "") + "-" + phone);
    } catch (ex) {}
  }

  return (
    <div className={`relative text-[15px] mt-2 ${className}`}>
      <FormControl
        variant="outlined"
        component="fieldset"
        className={`!border-primary/20 ${className} hover:!border-primary !rounded-md !flex !nowrap !p-2 -translate-y-2`}
        sx={{ border: 1 }}
        required={required}
      >
        {/* <legend className="!px-2 text-xs">
          {label !== undefined ? label : "Phone Number"} {required ? "*" : ""}
        </legend> */}
        <div className="flex justify-end items-center">
          <input
            className={`!w-[60px] !px-2 py-1 flex-none cursor-pointer bg-inherit ${
              size === "small" ? "!py-0" : ""
            }`}
            disabled={disabled}
            name="phoneCode"
            value={`+${code || "234"}`}
            tabIndex={0}
            onChange={() => true}
            onFocus={() => {
              setOpen(true);
              setTimeout(() => {
                sqRef.current && sqRef.current.focus();
              }, 100);
            }}
          />

          <input
            className="!flex-1 !border-0 bg-inherit"
            name="phone"
            ref={phoneRef}
            disabled={disabled}
            placeholder={placeholder || "940067966"}
            value={phone || ""}
            maxLength={10}
            tabIndex={0}
            onChange={(ev) => {
              try {
                onChange(code + "-" + ev.target.value);
              } catch (ex) {}
            }}
            // size={size}
            // label={label || 'Enter phone number'}
          />
        </div>
      </FormControl>

      <FormGroup
        className={`z-10 absolute top-[calc(100%+10px)] left-0 !bg-white drop-shadow p-4 -my-4 w-full border border-primary/20 ${
          open ? "" : "!hidden"
        }`}
      >
        <FormControl variant="outlined">
          <InputLabel shrink className="bg-white px-2">
            Country
          </InputLabel>
          <Autocomplete
            className="min-w-[200px]"
            noOptionsText="No data"
            disableClearable
            freeSolo
            options={option}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name
            }
            onChange={(ev, newVal) => {
              if (typeof newVal === "string") {
                handleChange(null);
              } else {
                handleChange(newVal.countryCallingCodes[0] || null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                // size={size}
                inputRef={sqRef}
                InputProps={{
                  ...params.InputProps,
                  type: "search",
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            renderOption={(props, opt) => {
              return (
                <li
                  {...props}
                  key={opt.alpha2 || opt.name}
                  className="flex flex-col cursor-pointer w-full px-4 py-2"
                  style={{ width: "100%" }}
                >
                  <h5>{opt.countryCallingCodes}</h5>
                  <small
                    className="whitespace-nowrap text-ellipsis block"
                    title={opt.name}
                  >
                    {opt.name}
                  </small>
                </li>
              );
            }}
          />
        </FormControl>
      </FormGroup>
    </div>
  );
}
